import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { IPostUpdate } from '../models/post-update.interface';
import { Observable, catchError, map, throwError, timeout } from 'rxjs';
import { IPostData } from '../models/post.interface';
import { IImageSearchResult } from '../models/google/image-search-result.interface';
import { ISocialCardResult } from '../models/social-card-result';
import { IMetric } from '../models/metric.interface';
import { IMetricAggregated } from '../models/metric-aggregated.interface';


@Injectable({
  providedIn: 'root',
})
export class PostService {
  private apiUrl: string = environment.apiUrl;

  constructor(private http: HttpClient) { }

  /**
   * Get post by id
   * @param postId post id
   * @returns 
   */
  getPostById(postId: string): Observable<IPostData> {
    const url = `${this.apiUrl}/posts/${postId}`;
    return this.http.get<any>(url);
  }


  /**
   * Get paginated posts for a specific user
   * @param userId The ID of the user
   * @param page The current page
   * @param limit Number of posts per page
   * @returns An Observable of paginated posts
   */
  getPostsByUser(userId: string, page: number, limit: number): Observable<{ posts: IPostData[], total: number }> {
    const url = `${this.apiUrl}/posts/user/${userId}?page=${page}&limit=${limit}`;
    return this.http.get<{ posts: IPostData[], total: number }>(url);
  }


  /**
   * Get scheduled posts for a specific user
   * @param userId The ID of the user
   * @returns An Observable of the user's scheduled posts
   */
  getScheduledPostsByUser(userId: string): Observable<IPostData[]> {
    const url = `${this.apiUrl}/posts/scheduled/${userId}`;
    return this.http.get<IPostData[]>(url);
  }

  /**
   * Get all unresolved failed posts across user's accessible accounts
   */
  getFailedPosts(): Observable<IPostData[]> {
    const url = `${this.apiUrl}/posts/failed`;
    return this.http.get<IPostData[]>(url);
  }

  /**
   * Get paginated posts for a specific account
   * @param accountId The ID of the account
   * @param page The current page
   * @param limit Number of posts per page
   * @param sort Optional ranking: 'engagement' | 'reach' | 'recent'
   * @param days Optional metric window in days (used with sort)
   */
  getPostsByAccount(accountId: string, page: number, limit: number, sort?: 'engagement' | 'reach' | 'recent', days?: number): Observable<{ posts: IPostData[], total: number }> {
    let url = `${this.apiUrl}/posts/account/${accountId}?page=${page}&limit=${limit}`;
    if (sort) { url += `&sort=${encodeURIComponent(sort)}`; }
    if (days) { url += `&days=${days}`; }
    return this.http.get<{ posts: IPostData[], total: number }>(url);
  }

  /**
   * Get scheduled posts for a specific account
   * @param accountId The ID of the account
   */
  getScheduledPostsByAccount(accountId: string): Observable<IPostData[]> {
    const url = `${this.apiUrl}/posts/scheduled/account/${accountId}`;
    return this.http.get<IPostData[]>(url);
  }


  /**
   * Publish a new post
   * @param post 
   * @param file 
   * @returns 
   */
  publish(post: IPostUpdate, file?: File): Observable<any> {
    const url = `${this.apiUrl}/posts`;
    console.log('Preparing to publish post:', post.title);  // Log the title of the post being published

    if (!post.title) {
      throw Error('Post title cannot be empty.');
    }
    if (!post.status) {
      throw Error('Post status cannot be empty.');
    }

    const formData = new FormData();

    if (file) {
      formData.append('image', file);
      console.log('Image file appended for upload');
    }

    formData.append('title', post.title);
    formData.append('body', post.body);
    formData.append('categories', JSON.stringify(post.categories));
    formData.append('userId', post.userId);
    formData.append('accountId', post.accountId);
    if (post.status === 'scheduled') {
      formData.append('status', post.status);
    }
    if (post.date) {
      formData.append('date', post.date);
    }
    if (post.image) {
      formData.append('image', post.image);
    }
    if (post.featuredUrl) {
      formData.append('featuredUrl', post.featuredUrl);
    }
    if (post.socialCard) {
      formData.append('socialCard', JSON.stringify(post.socialCard));
    }
    if (post.postType) {
      formData.append('postType', post.postType);
    }

    formData.append('active', JSON.stringify(post.active));

    console.log('Post data appended:', { title: post.title, body: post.body.length, categories: post.categories.length }); // Log details about the post content

    if (post.publishTargets) {
      formData.append('publishTargets', JSON.stringify(post.publishTargets));
      console.log('Publish targets appended:', post.publishTargets);
    }
    if (post.aggregateFeedId) {
      formData.append('aggregateFeedId', post.aggregateFeedId);
      console.log('Publish targets appended:', post.publishTargets);
    }

    console.log('Sending request to server...');
    return this.http.post(url, formData);
  }


  /**
   * Publish a new wordpress blog
   * @param post 
   * @param file 
   * @returns 
   */
  blog(post: IPostUpdate, file?: File): Observable<any> {
    if (!post.title) {
      throw Error('Post title cannot be empty.');
    }
    if (!post.status) {
      throw Error('Post status cannot be empty.');
    }

    const url = `${this.apiUrl}/posts/blog`;
    console.log('Preparing to publish blog:', post.title);  // Log the title of the post being published

    const formData = new FormData();

    if (file) {
      formData.append('image', file);
      console.log('Image file appended for upload');
    }

    formData.append('title', post.title);
    formData.append('body', post.body);
    formData.append('categories', JSON.stringify(post.categories));
    console.log('Post data appended:', { title: post.title, body: post.body.length, categories: post.categories.length }); // Log details about the post content

    if (post.featuredUrl) {
      formData.append('featuredUrl', post.featuredUrl);
    }

    formData.append('active', JSON.stringify(post.active));

    if (post.publishTargets) {
      formData.append('publishTargets', JSON.stringify(post.publishTargets));
      console.log('Publish targets appended:', post.publishTargets);
    }
    if (post.aggregateFeedId) {
      formData.append('aggregateFeedId', post.aggregateFeedId);
      console.log('Publish targets appended:', post.publishTargets);
    }

    console.log('Sending request to server...');
    return this.http.post(url, formData);
  }


  /**
  * Upload an image to the server.
  * @param file The image file to upload.
  */
  uploadImage(file: File): Observable<any> {
    const url: string = `${this.apiUrl}/posts/upload-image`;
    console.log(url);

    const formData = new FormData();
    formData.append('image', file);

    console.log('Uploading image to server...');
    return this.http.post(url, formData);
  }


  /**
   * Capture a screeshot at the url
   */
  captureScreenshot(url: string): Observable<any> {
    const body = { url: url };
    return this.http.post<{ message: string, path: string }>(`${this.apiUrl}/posts/screenshot`, body)
      .pipe(
        map(response => response.path)
      );
  }


    /**
   * Get metrics for a specific post (last N days, default 7)
   */
  getMetricsByPostId(postId: string, days: number = 7): Observable<IMetricAggregated[]> {
    const url = `${this.apiUrl}/posts/${postId}/metrics?days=${days}`;
    return this.http.get<IMetricAggregated[]>(url);
  }

  /**
   * Get publish-target usage/limits for the authenticated user (24h / 30d)
   * @param targetId e.g. 'twitter'
   */
  getPublishTargetUsage(targetId: string): Observable<{
    target: string;
    count24h: number;
    dailyLimit: number;
    remainingDaily: number;
    count30d: number;
    monthlyLimit: number;
    remainingMonthly: number;
  }> {
    const url = `${this.apiUrl}/posts/limits/${encodeURIComponent(targetId)}`;
    return this.http.get<{
      target: string;
      count24h: number;
      dailyLimit: number;
      remainingDaily: number;
      count30d: number;
      monthlyLimit: number;
      remainingMonthly: number;
    }>(url);
  }

  /**
   * Cancel a scheduled post
   * @param postId The ID of the scheduled post
   */
  cancelScheduledPost(postId: string): Observable<any> {
    const url = `${this.apiUrl}/posts/${postId}/cancel`;
    return this.http.post(url, {});
  }


  /**
   * Search images
   * @param query The search query
   * @param filter The filter to apply (e.g., "all", "images", "videos")
   * @returns Observable of IImageSearchResult[]
   */
  searchImages(query: string, filter: string): Observable<IImageSearchResult> {
    const url = `${this.apiUrl}/posts/image/search`;
    return this.http.post<IImageSearchResult>(url, { query, filter });
  }


  /**
   * Verify that the API server can download and process a candidate image URL.
   */
  verifyImageDownloadable(imageUrl: string): Observable<{
    downloadable: boolean;
    code?: string;
    status?: number;
    message?: string;
  }> {
    const url = `${this.apiUrl}/posts/image/verify-downloadable`;
    return this.http.post<{
      downloadable: boolean;
      code?: string;
      status?: number;
      message?: string;
    }>(url, { imageUrl });
  }


      /**
   * Create a text overlay on an image (may be long-running)
   * @param post IPostUpdate containing image path and overlay details
   */
  socialCard(post: IPostUpdate): Observable<ISocialCardResult> {
    const url = `${this.apiUrl}/posts/image/social-card`;

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    const timeoutDuration = 300000; // 5 minutes

    return this.http.post<ISocialCardResult>(url, post, options).pipe(
      timeout(timeoutDuration),
      catchError((error) => {
        console.error('Error details:', {
          name: error?.name,
          message: error?.message,
          status: error?.status,
          error,
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * Request a quick preview social card from the API.
   * @param payload Minimal payload: { title, image, accountId } or full IPostUpdate
   */
  previewSocialCard(payload: any): Observable<ISocialCardResult> {
    const url = `${this.apiUrl}/posts/image/social-card/preview`;
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };
    const timeoutDuration = 120000; // 2 minutes
    return this.http.post<ISocialCardResult>(url, payload, options).pipe(
      timeout(timeoutDuration),
      catchError((error) => {
        console.error('Preview generation error:', error);
        return throwError(() => error);
      })
    );
  }


  /**
   * Delete a post by its ID.
   * This API endpoint will delete the post from all connected social media.
   * @param postId The ID of the post to delete.
   */
  deletePost(postId: string): Observable<any> {
    const url = `${this.apiUrl}/posts/${postId}`; 
    return this.http.delete(url);
  }

  /**
   * Edit a Facebook post by its ID.
   * @param postId The ID of the post to edit.
   * @param content The new content for the post.
   */
  editFacebookPost(postId: string, content: string): Observable<any> {
    const url = `${this.apiUrl}/posts/${postId}/facebook`;
    return this.http.patch(url, { content });
  }

  /**
   * Get the text content of a Facebook post by its ID.
   * @param postId The ID of the post.
   * @returns An Observable containing the text content of the post.
   */
  getFacebookPostText(postId: string): Observable<{ text: string }> {
    const url = `${this.apiUrl}/posts/${postId}/facebook/text`;
    return this.http.get<{ text: string }>(url);
  }

  /**
   * Retry publishing a specific target for a post.
   * @param postId The ID of the post.
   * @param target The target to retry (e.g., 'bluesky').
   */
  retryPost(postId: string, target: string): Observable<any> {
    const url = `${this.apiUrl}/posts/${postId}/retry`;
    return this.http.post(url, { target });
  }

  /**
   * Retry publishing a specific target for a post with updated content.
   * @param postId The ID of the post.
   * @param target The target to retry (e.g., 'bluesky').
   * @param posts Array of post texts to publish for the target.
   */
  retryPostWithContent(postId: string, target: string, posts: string[]): Observable<any> {
    const url = `${this.apiUrl}/posts/${postId}/retry`;
    return this.http.post(url, { target, posts });
  }


  /**
   * 
   */
  async getPostCount(target?: string): Promise<number> {
    const url = `${this.apiUrl}/posts/count`;
    const response = await this.http.get<{ count: number }>(url, {
      params: { target },
    }).toPromise();
    return response?.count || 0;
  }
}
